import React, {useState} from 'react';
import { Link } from 'react-router-dom';
import { getComponentTypeFallbackIconPath } from "../../Helpers/componentTypeIconUtils";
import { useBuildComponentTypes } from "../../contexts/BuildComponentTypesContext";
import "./BuildingCustomButton.css";

interface CustomButtonProps {
    title: string;
    /** One line each; a node (e.g. text plus an icon) is rendered as-is. */
    metadata: React.ReactNode[];
    /** Renders a link to this path (Cmd/Ctrl-click opens a new tab); `onClick` still runs first. */
    to?: string;
    onClick?: () => void;
    imagePath?: string;
    componentType?: string; // Optional: if provided, will use fallback logic
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    isHovered?: boolean;
    style?: React.CSSProperties;
    dimmed?: boolean;
    disabled?: boolean;
    className?: string;
}

const BuildingCustomButton: React.FC<CustomButtonProps> = ({
                                                               title,
                                                               metadata,
                                                               to,
                                                               onClick,
                                                               imagePath,
                                                               componentType,
                                                               onMouseEnter,
                                                               onMouseLeave,
                                                               style,
                                                               dimmed = false,
                                                               disabled = false,
                                                               className = ''
                                                           }) => {
    const { types: buildComponentTypes } = useBuildComponentTypes();
    const [currentImagePath, setCurrentImagePath] = useState<string | undefined>(imagePath);
    const [hasTriedFallback, setHasTriedFallback] = useState(false);

    // Handle image load error - fallback to BaseType icon or placeholder
    const handleImageError = () => {
        if (!hasTriedFallback && componentType) {
            const fallbackPath = getComponentTypeFallbackIconPath(componentType, buildComponentTypes);
            setCurrentImagePath(fallbackPath);
            setHasTriedFallback(true);
        } else {
            // Final fallback to placeholder
            setCurrentImagePath('placeholder.png');
        }
    };

    // Reset fallback state when imagePath or componentType changes
    React.useEffect(() => {
        setCurrentImagePath(imagePath);
        setHasTriedFallback(false);
    }, [imagePath, componentType]);

    const content = (
        <>
            {currentImagePath && (
                <img
                    src={`/Images/${currentImagePath}`}
                    alt={title}
                    style={{
                        width: '40px',
                        height: '40px',
                        objectFit: 'cover',
                        borderRadius: '0px',
                        margin: '5px',
                        opacity: disabled ? 0.5 : 1,
                        filter: dimmed || disabled ? 'grayscale(1)' : undefined,
                    }}
                    onError={handleImageError}
                />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '10px' }}>
                <strong style={{
                    fontSize: '16px',
                    marginBottom: '5px',
                    color: disabled ? '#999' : 'inherit'
                }}>
                    {title}
                </strong>
                <span style={{
                    fontSize: '14px',
                    color: disabled ? '#999' : '#777'
                }}>
                    {metadata.map((text, index) => {
                        const isPositionNote = text === 'Position ej markerad på ritning';
                        return (
                            <React.Fragment key={index}>
                                {isPositionNote ? (
                                    <em style={{ color: '#000' }}>{text}</em>
                                ) : (
                                    text
                                )}
                                <br/>
                            </React.Fragment>
                        );
                    })}
                </span>
            </div>
        </>
    );

    const sharedProps = {
        className: `dropshadow dropshadow-hover building-custom-button ${className}`,
        onMouseEnter,
        onMouseLeave,
        style: {
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.6 : dimmed ? 0.45 : 1,
            ...style
        },
    };

    if (to && !disabled) {
        return <Link to={to} onClick={onClick} {...sharedProps}>{content}</Link>;
    }

    return (
        <button
            {...sharedProps}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
        >
            {content}
        </button>
    );
};

export default BuildingCustomButton;